import type { AxiosRequestConfig } from 'axios';
import { apiService, CHECKOUT_API_BASE_URL, CHECKOUT_API_CONFIG } from '../../services/apiService';
import { API_PATHS } from '../../utils/apiPaths';

export const MONEEU_CHECKOUT_MESSAGE_SOURCE = 'moneyeu-checkout';

// Both brands are listed because ONE build serves both: a merchant embedding a white-label
// checkout gets a login.bemi.uk URL, and leaving those out would make isAllowedCheckoutUrl reject
// their own checkout and isTrustedCheckoutOrigin drop every PAYMENT_RESULT message it sends.
export const DEFAULT_MONEEU_CHECKOUT_ORIGINS = [
  'https://login.moneyeu.com',
  'https://dev.login.moneyeu.com',
  'https://login.bemi.uk',
  'https://dev.login.bemi.uk',
] as const;

export type MoneyEuCheckoutMessageType =
  | 'CHECKOUT_READY'
  | 'CHECKOUT_RESIZE'
  | 'PAYMENT_RESULT'
  | 'CHECKOUT_ERROR'
  | 'CHECKOUT_CANCEL';

export type MoneyEuCheckoutMessage = {
  source: typeof MONEEU_CHECKOUT_MESSAGE_SOURCE;
  type: MoneyEuCheckoutMessageType;
  transactionId?: string | null;
  status?: string | null;
  message?: string | null;
  height?: number;
};

export type VerifiedPaymentStatus = {
  transactionId: string;
  status: string;
  amount: number | null;
  currency: string | null;
  message: string | null;
  reason: string | null;
};

function readString(value: unknown): string | null {
  if (value === undefined || value === null || value === '') return null;
  return String(value);
}

function readNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function getTrustedCheckoutOrigins(): string[] {
  const origins = new Set<string>(DEFAULT_MONEEU_CHECKOUT_ORIGINS);
  if (typeof window !== 'undefined') {
    origins.add(window.location.origin);
  }
  const extra = import.meta.env.VITE_MONEEU_CHECKOUT_ORIGIN?.trim();
  if (extra) {
    try {
      origins.add(new URL(extra).origin);
    } catch {
      origins.add(extra.replace(/\/$/, ''));
    }
  }
  return [...origins];
}

export function toEmbeddableCheckoutUrl(checkoutUrl: string): string {
  try {
    const url = new URL(checkoutUrl.trim(), window.location.origin);
    if (!/^\/pay\/[A-Za-z0-9_-]+\/?$/.test(url.pathname)) return checkoutUrl.trim();
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return `${window.location.origin}${url.pathname}${url.search}${url.hash}`;
    }
    return url.toString();
  } catch {
    return checkoutUrl.trim();
  }
}

export function isEmbeddedCheckout(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  const embed = params.get('embed');
  if (embed === '1' || embed === 'true') return true;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export function withEmbedQuery(checkoutUrl: string): string {
  try {
    const url = new URL(checkoutUrl, window.location.origin);
    if (!url.searchParams.has('embed')) {
      url.searchParams.set('embed', '1');
    }
    return url.toString();
  } catch {
    return checkoutUrl;
  }
}

export function isAllowedCheckoutUrl(
  checkoutUrl: string,
  allowedOrigins: readonly string[] = getTrustedCheckoutOrigins(),
): boolean {
  try {
    const url = new URL(checkoutUrl, window.location.origin);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
    if (!/^\/pay\/[A-Za-z0-9_-]+\/?$/.test(url.pathname)) return false;
    return allowedOrigins.includes(url.origin);
  } catch {
    return false;
  }
}

export function isTrustedCheckoutOrigin(
  origin: string,
  allowedOrigins: readonly string[] = getTrustedCheckoutOrigins(),
): boolean {
  return allowedOrigins.includes(origin);
}

export function isMoneyEuCheckoutMessage(data: unknown): data is MoneyEuCheckoutMessage {
  if (!data || typeof data !== 'object') return false;
  const message = data as Record<string, unknown>;
  return (
    message.source === MONEEU_CHECKOUT_MESSAGE_SOURCE &&
    typeof message.type === 'string'
  );
}

/** Origin of the API host this build talks to — a relative base means it is our own origin. */
function ownApiOrigin(): string | null {
  try {
    return new URL(CHECKOUT_API_BASE_URL, window.location.origin).origin;
  } catch {
    return null;
  }
}

/**
 * Who to address a postMessage to — i.e. the origin of the page holding this frame.
 *
 * document.referrer was the only signal here, and it is wrong the moment this frame has navigated
 * even once: it names the PREVIOUS DOCUMENT, not the parent. On the embedded 3DS path it ends up
 * as api.moneyeu.com (the gateway-return page this frame came from), so every PAYMENT_RESULT was
 * addressed to an origin that is not the parent and the browser silently dropped it — the shopper
 * saw the result and the merchant's onSuccess never fired.
 *
 * ancestorOrigins[0] IS the immediate parent, by definition, however many times we have navigated.
 * Firefox doesn't implement it, hence the fallbacks, in descending order of trustworthiness. '*'
 * last: these messages carry the merchant's own order status to the merchant's own page, and a
 * dropped result is worse than a broadly-addressed one.
 */
function parentTargetOrigin(): string {
  try {
    const ancestors = (window.location as unknown as { ancestorOrigins?: DOMStringList }).ancestorOrigins;
    if (ancestors && ancestors.length > 0 && ancestors[0]) return ancestors[0];
  } catch {
    // Not implemented here — fall through.
  }
  try {
    const declared = new URLSearchParams(window.location.search).get('parentOrigin');
    if (declared) return new URL(declared).origin;
  } catch {
    // Malformed parentOrigin — fall through.
  }
  try {
    if (document.referrer) {
      const referrerOrigin = new URL(document.referrer).origin;
      // Only useful while it is someone ELSE's page. Our own hosts here mean we navigated within
      // the frame (checkout → redirect → gateway-return), so it says nothing about the parent.
      // The API host is the one that actually bites: it is where gateway-return lives, and it is
      // NOT in getTrustedCheckoutOrigins, so it would otherwise pass this filter unnoticed.
      if (
        referrerOrigin !== window.location.origin &&
        !isTrustedCheckoutOrigin(referrerOrigin) &&
        referrerOrigin !== ownApiOrigin()
      ) {
        return referrerOrigin;
      }
    }
  } catch {
    // Ignore malformed referrers and fall back below.
  }
  return '*';
}

export function postCheckoutMessage(message: Omit<MoneyEuCheckoutMessage, 'source'>): void {
  if (typeof window === 'undefined' || !isEmbeddedCheckout()) return;
  const payload: MoneyEuCheckoutMessage = {
    source: MONEEU_CHECKOUT_MESSAGE_SOURCE,
    ...message,
  };
  window.parent.postMessage(payload, parentTargetOrigin());
}

/**
 * Turns the redirect URL the backend hands back into the URL this iframe should actually load.
 *
 * API/3DS paths are left on whatever host the backend named — normally the API host, which is a
 * different origin from this checkout page. That used to be impossible to frame, so this rewrote
 * them onto the current origin and relied on something there proxying `/moneyEu/*` back to the
 * API: Vite does in dev, nothing did in prod, so the SPA answered with its own shell and the
 * shopper landed on /404 (HAR, 2026-09-26: `login.moneyeu.com/moneyEu/api/v1/payments/redirect/
 * TXN-…` came back as text/html). The backend now answers those paths with
 * `Content-Security-Policy: frame-ancestors` naming this host instead of a blanket
 * X-Frame-Options, so the real cross-origin URL frames correctly — see
 * com.moneyeu.service.payment.FrameAncestorsPolicy. A path that is ALREADY same-origin (the Vite
 * dev proxy) still gets collapsed to a relative URL, which costs nothing and keeps dev working if
 * the backend there predates that header.
 *
 * Outside the embedded case nothing is rewritten at all: a masked payment/redirect URL is
 * *supposed* to be cross-origin for a plain top-level navigation — it deliberately lives on the
 * backend's own domain (or BEMI's) — and collapsing it to a host-less relative path landed the
 * shopper on this frontend's own 404 page.
 */
export function resolveFramedRedirectUrl(redirectUrl: string): string {
  try {
    const url = new URL(redirectUrl, window.location.origin);
    if (!isEmbeddedCheckout()) return url.toString();

    const statusMatch = url.pathname.match(/\/payments\/redirect\/status\/([^/]+)$/);
    let next: string;
    if (statusMatch) {
      next = `/payments/redirect/status/${encodeURIComponent(statusMatch[1])}${url.search}${url.hash}`;
    } else if (url.pathname.startsWith('/moneyEu/') && url.origin === window.location.origin) {
      next = `${url.pathname}${url.search}${url.hash}`;
    } else {
      next = url.toString();
    }

    const resolved = new URL(next, window.location.origin);
    if (!resolved.searchParams.has('embed')) {
      resolved.searchParams.set('embed', '1');
    }
    if (/^https?:/i.test(next)) return resolved.toString();
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch {
    return redirectUrl;
  }
}

export function unwrapVerifiedPaymentStatus(
  res: unknown,
  fallbackTransactionId: string,
): VerifiedPaymentStatus {
  let data: Record<string, unknown> =
    res && typeof res === 'object' ? (res as Record<string, unknown>) : {};

  if (data.data && typeof data.data === 'object' && !('transactionId' in data) && !('status' in data)) {
    data = data.data as Record<string, unknown>;
  }

  return {
    transactionId: String(data.transactionId ?? fallbackTransactionId),
    status: String(data.status ?? data.transactionStatus ?? 'PENDING'),
    amount: readNumber(data.amount),
    currency: readString(data.currency),
    message: readString(data.message),
    reason: readString(data.reason),
  };
}

/**
 * Best-effort, fire-and-forget capture of window.location.ancestorOrigins — the full framing
 * ancestor chain for an embedded checkout, set by the browser itself and unaffected by
 * Referrer-Policy (unlike document.referrer/the Referer header, which the backend already captures
 * server-side off the GET /checkout/orders/{token} request). Chromium/WebKit only; simply absent
 * (not an error) in Firefox or when the page isn't framed at all. Pure enrichment on the order —
 * see Order.capturedAncestorOrigins — never awaited by callers, never blocks the checkout UI, and
 * failing silently (old cached frontend hitting a backend that doesn't have this route yet, an
 * ad-blocker, a network blip) is an entirely acceptable outcome.
 *
 * Always fires, even with an empty/missing ancestorOrigins list — the backend needs to see the
 * call happen at all to tell "browser doesn't support this / page isn't framed" apart from "this
 * capture never ran" (see Order.ancestorOriginsCaptureAttemptedAt). nonce must come from
 * CheckoutOrderSummaryResponse.originCaptureNonce; the backend rejects (silently, from this
 * caller's perspective — see captureCheckoutOrigin) any call missing or misusing it.
 */
export function captureAncestorOrigins(token: string, nonce: string, config?: AxiosRequestConfig): void {
  if (typeof window === 'undefined' || !token || !nonce) return;
  const raw = (window.location as unknown as { ancestorOrigins?: { length: number } & Iterable<string> })
    .ancestorOrigins;
  const ancestorOrigins = raw && raw.length > 0 ? Array.from(raw) : [];
  apiService
    .post(
      `/checkout/orders/${token}/capture-origin`,
      { nonce, ancestorOrigins, documentReferrer: document.referrer || null },
      config,
    )
    .catch(() => {
      // Best-effort enrichment — never worth surfacing to the shopper.
    });
}

export async function verifyCheckoutPaymentStatus(
  transactionId: string,
): Promise<VerifiedPaymentStatus> {
  const res = await apiService.get<unknown>(
    `${API_PATHS.PAYMENT_REDIRECT_STATUS}/${encodeURIComponent(transactionId)}`,
    { ...CHECKOUT_API_CONFIG, withCredentials: false },
  );
  return unwrapVerifiedPaymentStatus(res, transactionId);
}
